import * as React from "react";
import { Box, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";

import { RunDetail } from "@/hooks/api/use-proving-ground";

import { MONO, standingOf, Verdict, verdictInk, verdictTint } from "../shared";

/**
 * A persona's standing on one behaviour, across however many scenarios it was run through.
 *
 * `gate` and `review` are kept apart deliberately. One failure among several is a thing to
 * look at; failing everywhere is a thing to stop a release for, and collapsing both into
 * "not passing" loses the only distinction that changes what you do next.
 */
type Standing = {
	passed: number;
	failed: number;
	pending: number;
	overridden: boolean;
	verdict: Verdict;
	cellIds: number[];
};

const cellSx = {
	p: "9px 8px",
	borderRight: 1,
	borderBottom: 1,
	borderColor: "divider",
	textAlign: "center" as const,
};

/** The small pill each verdict is rendered as. */
function Pill({ verdict, children }: { verdict: Verdict; children: React.ReactNode }): React.JSX.Element {
	return (
		<Box
			component="span"
			sx={[
				{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					minWidth: 46,
					height: 20,
					px: 0.75,
					borderRadius: 999,
					fontFamily: MONO,
					fontSize: 10,
					fontWeight: 600,
					letterSpacing: ".02em",
					fontVariantNumeric: "tabular-nums",
				},
				verdictTint(verdict),
			]}
		>
			{children}
		</Box>
	);
}

/**
 * The run grid: caller populations down, required behaviours across.
 *
 * Pivoted this way round because it answers the two questions worth asking. A column of
 * failures means a behaviour is broken for everyone, which is a prompt problem. A row of
 * failures means one caller the agent cannot handle, which is a coverage problem. A grid of
 * scenarios against personas, with each cell reading "3 of 4", answers neither.
 */
export function RunMatrix({
	run,
	onOpenCell,
}: {
	run?: RunDetail;
	onOpenCell: (cellId: number) => void;
}): React.JSX.Element {
	const [scenarioId, setScenarioId] = React.useState<string>("all");

	/**
	 * One row per call, not one per caller.
	 *
	 * Collapsing a caller's scenarios into a single row loses the thing the grid exists to
	 * show: "2 of 3" tells you something broke without telling you which situation broke it,
	 * and the situation is usually the answer. Viewing every scenario at once therefore lists
	 * every call, grouped under the scenario it belongs to; narrowing to one scenario drops
	 * the grouping, because then there is only one call per caller and the header would be
	 * repeating itself.
	 */
	const rows = React.useMemo(() => {
		const personas = run?.personas ?? [];
		if (scenarioId !== "all") {
			return personas.map((persona) => ({
				key: persona.id,
				scenarioId,
				personaId: persona.id,
				label: persona.name,
				group: null as string | null,
			}));
		}
		return (run?.scenarios ?? []).flatMap((scenario) =>
			personas.map((persona) => ({
				key: `${scenario.id}:${persona.id}`,
				scenarioId: scenario.id,
				personaId: persona.id,
				label: persona.name,
				group: scenario.name as string | null,
			}))
		);
	}, [run, scenarioId]);

	const grid = React.useMemo(() => {
		const map = new Map<string, Standing>();
		for (const row of rows) {
			const rowCells = (run?.cells ?? []).filter(
				(cell) => cell.scenarioId === row.scenarioId && cell.personaId === row.personaId
			);

			for (const metric of run?.metrics ?? []) {
				let passed = 0;
				let failed = 0;
				let pending = 0;
				let overridden = false;
				const ids: number[] = [];

				for (const cell of rowCells) {
					const verdict = cell.verdicts?.find((v) => v.metricId === metric.id);
					// Not-applicable counts toward neither side: a metric skipped for want of audio
					// must not make a text run look better than the voice run it stands in for.
					if (!verdict || verdict.notApplicable) {
						if (cell.status === "pending" || cell.status === "running") pending++;
						continue;
					}
					if (verdict.overridden) overridden = true;
					if (verdict.passed) passed++;
					else failed++;
					ids.push(cell.id);
				}

				map.set(`${row.key}:${metric.id}`, {
					passed,
					failed,
					pending,
					overridden,
					verdict: standingOf(passed, failed),
					// Every call on this row, not only the ones that produced a verdict. A cell that
					// failed to run has no verdicts at all, and the drawer is the only place its error
					// is shown — collecting ids from verdicts alone made those cells unopenable.
					cellIds: ids.length ? ids : rowCells.map((cell) => cell.id),
				});
			}
		}
		return map;
	}, [run, rows]);

	/** A row's overall standing: any behaviour gated gates the call. */
	const release = React.useMemo(() => {
		const map = new Map<string, Verdict>();
		for (const row of rows) {
			let worst: Verdict = "none";
			for (const metric of run?.metrics ?? []) {
				const cell = grid.get(`${row.key}:${metric.id}`);
				if (!cell || cell.verdict === "none") continue;
				if (cell.verdict === "gate") worst = "gate";
				else if (cell.verdict === "review" && worst !== "gate") worst = "review";
				else if (worst === "none") worst = "pass";
			}
			map.set(row.key, worst);
		}
		return map;
	}, [run, rows, grid]);

	/**
	 * Per-behaviour totals down every call shown. This is the number that distinguishes one
	 * awkward call from a behaviour that is broken everywhere, and reading it off a column of
	 * coloured squares is exactly the work this row removes.
	 */
	const columnTotals = React.useMemo(() => {
		const map = new Map<string, { passed: number; total: number }>();
		for (const metric of run?.metrics ?? []) {
			let passed = 0;
			let total = 0;
			for (const row of rows) {
				const cell = grid.get(`${row.key}:${metric.id}`);
				passed += cell?.passed ?? 0;
				total += (cell?.passed ?? 0) + (cell?.failed ?? 0);
			}
			map.set(metric.id, { passed, total });
		}
		return map;
	}, [run, rows, grid]);

	const metrics = run?.metrics ?? [];

	if (metrics.length === 0) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography variant="body2" color="text.secondary">
					No results yet. The grid fills in as calls are scored.
				</Typography>
			</Box>
		);
	}

	const headSx = {
		fontFamily: MONO,
		fontSize: 9,
		fontWeight: 500,
		letterSpacing: ".07em",
		textTransform: "uppercase" as const,
		color: "text.secondary",
	};

	return (
		<Box>
			<Stack
				direction="row"
				spacing={1.5}
				sx={{ alignItems: "center", px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}
			>
				<Typography sx={headSx}>Scenario</Typography>
				<Select
					size="small"
					value={scenarioId}
					onChange={(event) => setScenarioId(event.target.value)}
					sx={{ minWidth: 240, "& .MuiSelect-select": { fontSize: 12, py: 0.5 } }}
				>
					<MenuItem value="all" sx={{ fontSize: 12 }}>
						All scenarios
					</MenuItem>
					{(run?.scenarios ?? []).map((scenario) => (
						<MenuItem key={scenario.id} value={scenario.id} sx={{ fontSize: 12 }}>
							{scenario.name}
						</MenuItem>
					))}
				</Select>
				<Box sx={{ flex: "1 1 auto" }} />
				{(["pass", "review", "gate"] as Verdict[]).map((verdict) => (
					<Stack key={verdict} direction="row" spacing={0.6} sx={{ alignItems: "center" }}>
						<Box
							sx={[
								{ width: 9, height: 9, borderRadius: 999 },
								// A solid fill, not the pill's tint. `verdictTint` is a ground designed to sit
								// under text at 12% alpha; on a bare dot with nothing on it, the legend for
								// the whole grid was invisible against the card.
								(theme: any) => ({
									bgcolor:
										verdict === "pass"
											? theme.vars.palette.success.main
											: verdict === "review"
												? theme.vars.palette.warning.main
												: theme.vars.palette.error.main,
								}),
							]}
						/>
						<Typography sx={headSx}>{verdict}</Typography>
					</Stack>
				))}
			</Stack>

			{/* Wide grids scroll inside the card rather than pushing the page sideways. */}
			<Box sx={{ overflowX: "auto" }}>
				<Box
					component="table"
					sx={{
						width: "100%",
						minWidth: 820,
						borderCollapse: "collapse",
						tableLayout: "fixed",
					}}
				>
					<thead>
						<Box component="tr">
							<Box
								component="th"
								sx={{
									...cellSx,
									...headSx,
									width: 230,
									height: 88,
									textAlign: "left",
									verticalAlign: "bottom",
								}}
							>
								{scenarioId === "all" ? "Call" : "Caller population"}
							</Box>
							{metrics.map((metric) => (
								<Box
									key={metric.id}
									component="th"
									sx={{ ...cellSx, ...headSx, height: 88, verticalAlign: "bottom", lineHeight: 1.35 }}
								>
									{metric.name}
								</Box>
							))}
							<Box
								component="th"
								sx={{ ...cellSx, ...headSx, width: 92, height: 88, verticalAlign: "bottom", borderRight: 0 }}
							>
								Release
							</Box>
						</Box>
					</thead>

					<tbody>
						{rows.map((row, index) => (
							<React.Fragment key={row.key}>
								{/* A scenario band, emitted when the grouping changes rather than on every row. */}
								{row.group && row.scenarioId !== rows[index - 1]?.scenarioId ? (
									<Box component="tr">
										<Box
											component="th"
											colSpan={metrics.length + 2}
											sx={{
												...cellSx,
												...headSx,
												textAlign: "left",
												borderRight: 0,
												bgcolor: "var(--mui-palette-background-level1)",
												fontSize: 10,
												color: "text.primary",
											}}
										>
											{row.group}
										</Box>
									</Box>
								) : null}

								<Box component="tr">
									<Box component="th" sx={{ ...cellSx, textAlign: "left", verticalAlign: "middle" }}>
										<Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{row.label}</Typography>
									</Box>

									{metrics.map((metric) => {
										const cell = grid.get(`${row.key}:${metric.id}`);
										const total = (cell?.passed ?? 0) + (cell?.failed ?? 0);

										return (
											<Box component="td" key={metric.id} sx={cellSx}>
												<Tooltip
													arrow
													title={
														total
															? `${metric.name} — ${cell!.passed} of ${total} passed${
																	cell!.overridden ? " · a verdict was overruled" : ""
																}`
															: cell?.pending
																? "Still running"
																: "Not scored"
													}
												>
													<Box
														onClick={() => cell?.cellIds.length && onOpenCell(cell.cellIds[0])}
														sx={{
															display: "inline-flex",
															alignItems: "center",
															gap: 0.5,
															cursor: cell?.cellIds.length ? "pointer" : "default",
														}}
													>
														<Pill verdict={cell?.verdict ?? "none"}>
															{total ? `${cell!.passed}/${total}` : cell?.pending ? "···" : "—"}
														</Pill>
														{/* A human corrected the judge here — visible without opening the call. */}
														{cell?.overridden ? (
															<Box sx={{ width: 4, height: 4, borderRadius: 999, bgcolor: "text.secondary" }} />
														) : null}
													</Box>
												</Tooltip>
											</Box>
										);
									})}

									<Box component="td" sx={{ ...cellSx, borderRight: 0 }}>
										<Typography
											component="b"
											sx={[
												{
													fontFamily: MONO,
													fontSize: 9,
													fontWeight: 700,
													letterSpacing: ".08em",
													textTransform: "uppercase",
												},
												verdictInk(release.get(row.key) ?? "none"),
											]}
										>
											{release.get(row.key) ?? "—"}
										</Typography>
									</Box>
								</Box>
							</React.Fragment>
						))}
					</tbody>

					<tfoot>
						<Box component="tr">
							<Box component="th" sx={{ ...cellSx, ...headSx, textAlign: "left", borderBottom: 0 }}>
								{scenarioId === "all" ? "All calls" : "All callers"}
							</Box>
							{metrics.map((metric) => {
								const total = columnTotals.get(metric.id);
								const rate = total?.total ? Math.round((total.passed / total.total) * 100) : null;
								return (
									<Box component="td" key={metric.id} sx={{ ...cellSx, borderBottom: 0 }}>
										<Typography
											sx={[
												{
													fontFamily: MONO,
													fontSize: 11,
													fontWeight: 600,
													fontVariantNumeric: "tabular-nums",
												},
												// An array, not a spread: these are theme callbacks, and spreading one into
												// an object literal yields nothing and silently drops the colour.
												rate === null
													? { color: "text.secondary" }
													: verdictInk(rate === 100 ? "pass" : rate === 0 ? "gate" : "review"),
											]}
										>
											{rate === null ? "—" : `${rate}%`}
										</Typography>
									</Box>
								);
							})}
							<Box component="td" sx={{ ...cellSx, borderBottom: 0, borderRight: 0 }} />
						</Box>
					</tfoot>
				</Box>
			</Box>
		</Box>
	);
}
